import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  ActivityBodyInterface,
  TimeslotStatusEnum,
} from '@sport-activities/sdk';
import { add, formatISODuration, intervalToDuration } from 'date-fns';
import moment from 'moment-timezone';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import * as TzLookup from 'tz-lookup';
import { appConfig } from '~/app.config';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { CheckboxAlignEnum } from '~/component/form/checkbox/enums/checkbox-align.enum';
import { ModalRoleEnum } from '~/component/modal/enums/role.enum';
import { ModalComponent } from '~/component/modal/modal.component';

import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { parseISO8601Duration } from '~/core/helpers/date/parse-iso-duration/parse-iso-duration.helper';
import { timeToDate } from '~/core/helpers/date/time-to-date/time-to-date.helper';
import { getRefundTimeLimit } from '~/core/helpers/refund-time-limit/refund-time-limit.helper';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';

@Component({
  selector: 'app-organizer-activity-edition-duration',
  templateUrl: './duration.component.html',
})
export class OrganizerActivityEditionDurationComponent
  implements OnInit, OnDestroy
{
  @ViewChild('confirmBookableUntilTheEndModal')
  public confirmBookableUntilTheEndModal: ModalComponent;
  private readonly subscriptions = new Subscription();

  public readonly checkboxAlign = CheckboxAlignEnum.Top;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly modalRole = ModalRoleEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public activity: Activity;
  public formGroup: FormGroup;
  public isFormValid: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public timeslots: Timeslot[] = [];

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    protected readonly organizerSharedActivityService: OrganizerSharedActivityService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.formGroup = new FormGroup(
      {
        timeDuration: new FormControl(
          { hours: 1, minutes: 0 },
          Validators.required
        ),
        dayDuration: new FormControl(1, Validators.required),
        bookableUntilTheEnd: new FormControl(false),
      },
      this.isTimeValid()
    );

    this.subscriptions.add(
      this.formGroup.valueChanges.subscribe(() => this.updateFormValidity())
    );

    this.subscriptions.add(
      this.activatedRoute.data.subscribe(({ activityData }) => {
        this.activity = activityData.activity;

        if (!this.activity.duration) {
          return;
        }

        const { days, hours, minutes } = parseISO8601Duration(
          this.activity.duration
        ).duration;
        this.formGroup.get('timeDuration')?.setValue({ hours, minutes });
        this.formGroup.get('dayDuration')?.setValue(days + 1);
        this.formGroup
          .get('bookableUntilTheEnd')
          ?.setValue(this.activity.bookableUntilTheEnd);
      })
    );

    if (this.isCourseType || this.isEventType) {
      this.formGroup.addControl(
        'courseStartTime',
        new FormControl('', Validators.required)
      );
      this.formGroup.addControl(
        'courseEndTime',
        new FormControl('', Validators.required)
      );
    }

    this.subscriptions.add(
      this.organizerSharedActivityService
        .getTimeslots({
          activityIdentifier: [Number(this.activity?.sku)],
          timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
          startDate: new Date().toISOString(),
          pagination: {
            from: 0,
            limit: 2000,
          },
        })
        .subscribe((response) => {
          this.timeslots = response.items;
        })
    );
  }

  public get hasInitialValue(): boolean {
    return Boolean(this.activity?.duration);
  }

  public get isCourseType(): boolean {
    return this.activity?.isCourse;
  }

  public get isEventType(): boolean {
    return this.activity?.isEvent;
  }

  public get isLive(): boolean {
    return this.activity.isLive;
  }

  public get tipsLabelKeys(): { text: string; title: string } {
    return this.isCourseType || this.isEventType
      ? {
          title: 'organizer.edit.duration.tips.time_duration_course.title',
          text: 'organizer.edit.duration.tips.time_duration_course.text',
        }
      : {
          title: 'organizer.edit.duration.tips.time_duration.title',
          text: 'organizer.edit.duration.tips.time_duration.text',
        };
  }

  private buildDataBeforeSubmit(): Partial<ActivityBodyInterface> {
    const refundTimeLimit = getRefundTimeLimit(
      this.activity,
      appConfig.refundLimit
    );

    const bookableUntilTheEnd = this.formGroup.get('bookableUntilTheEnd').value;

    // We cannot retrieve previous start/end time, so if left empty we use old value
    if (
      (this.isCourseType || this.isEventType) &&
      this.formGroup.get('courseStartTime')?.valid &&
      this.formGroup.get('courseEndTime')?.valid
    ) {
      const { hours: courseHours, minutes: courseMinutes } = intervalToDuration(
        {
          start: timeToDate(this.formGroup.get('courseStartTime').value),
          end: timeToDate(this.formGroup.get('courseEndTime').value),
        }
      );
      return {
        duration: formatISODuration({
          days: this.formGroup.get('dayDuration')?.value - 1,
          hours: courseHours,
          minutes: courseMinutes,
        }),
        refundTimeLimit,
        bookableUntilTheEnd,
      };
    }
    const { hours, minutes } = this.formGroup.get('timeDuration').value;
    return {
      duration: formatISODuration({
        days: this.formGroup.get('dayDuration')?.value - 1,
        hours,
        minutes,
      }),
      refundTimeLimit,
      bookableUntilTheEnd,
    };
  }

  public closeConfirmBookableUntilTheEndModal(): void {
    this.confirmBookableUntilTheEndModal.hide();
  }

  public showConfirmBookableUntilTheEndModal(): void {
    this.confirmBookableUntilTheEndModal.show();
  }

  private isTimeValid(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.get('timeDuration').value.hours +
        control.get('timeDuration').value.minutes >
        0
        ? null
        : { invalidDuration: true };
    };
  }

  public updateDuration(): void {
    this.closeConfirmBookableUntilTheEndModal();
    this.isOverlayDisplayed = true;
    const partialBody = this.buildDataBeforeSubmit();

    const observables$: Observable<Activity | Timeslot[]>[] = [
      this.organizerSharedActivityService
        .patchActivityById(this.activity?.sku, partialBody)
        .pipe(
          mergeMap(() => {
            return this.organizerSharedActivityService
              .getActivity(this.activity?.sku.toString())
              .pipe(
                repeatUntil(
                  (activity) => activity.duration === partialBody.duration
                )
              );
          })
        ),
    ];

    if (this.timeslots?.length) {
      const timeslotsToUpdate = this.timeslots.map((timeslot) => {
        const timezoneString = this.isLive
          ? moment?.tz.guess()
          : TzLookup(
              this.activity?.location?.geo?.latitude,
              this.activity?.location?.geo?.longitude
            );

        const newStartDate = moment
          .tz(timeslot.startDate, timezoneString)
          .format();

        return {
          identifier: timeslot.identifier,
          endDate: moment
            .tz(
              add(
                new Date(newStartDate),
                parseISO8601Duration(partialBody.duration).duration
              ).toISOString(),
              timezoneString
            )
            .format(),
        };
      });

      observables$.push(
        this.organizerSharedActivityService
          .patchTimeslotBulk(this.activity?.sku?.toString(), timeslotsToUpdate)
          .pipe(
            mergeMap(() => {
              return this.organizerSharedActivityService.getTimeslots({
                activityIdentifier: [Number(this.activity.sku)],
                timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
                startDate: new Date().toISOString(),
              });
            }),
            map((response) => response.items)
          )
      );
    }
    this.subscriptions.add(
      combineLatest(observables$).subscribe(() => {
        this.overlayStatus = SuccessStatusEnum.SUCCESS;
      })
    );
  }

  public updateFormValidity(): void {
    this.isFormValid =
      (this.formGroup.valid ||
        (this.formGroup.get('dayDuration')?.valid &&
          // We cannot retrieve previous start/end time, so if left empty we don't update the value
          !this.formGroup.get('courseStartTime')?.valid &&
          !this.formGroup.get('courseEndTime')?.valid &&
          this.formGroup.get('timeDuration')?.valid)) &&
      this.formGroup.dirty;
  }
}
